import "./assets/less/main.less";
import "./assets/less/uistruct.less";

import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import { ref } from "vue";
import AOS from "aos";
import "aos/dist/aos.css";
import { Skapi, SkapiError } from "skapi-js";
import { initUser, userLoginCallback } from "./code/user";
import {
    fullScreenLoader,
    removeFullScreenLoader,
} from "@/components/jsComponents";
import { trackAutoBind } from "@/assets/js/event";
import { user } from "@/code/user";
import { SourceMapConsumer } from "source-map-js";

let autoLogin = window.localStorage.getItem("dont_remember") !== "true";

const SERVICE_ID = import.meta.env.VITE_ADMIN;
const ETC_CONFIG = JSON.parse(import.meta.env.VITE_ETC);

export const connected = ref(false);
let appMounted = false;

// build date
console.log("deploy:" + import.meta.env.VITE_DATE);

const app = createApp(App);

// Get the latest version number of skapi-js from npm
let verAPI = "https://registry.npmjs.org/skapi-js/latest";
export const npmVersion = ref("...");

fetch(verAPI)
    .then((res) => res.json())
    .then((data) => {
        npmVersion.value = data.version;
    });

export const skapi = new Skapi(
    SERVICE_ID,
    "skapi",
    {
        requestBatchSize: 50,
        autoLogin,
        eventListener: {
            // onBatchProcess: (b) => console.log("Batch Process:", b),
            onUserUpdate: (user) => {
                initUser(user);
            },
            onLogin: async (user) => {
                if (!user && connected.value) {
                    skapi.util.terminatePendingRequests();
                }

                await userLoginCallback(user);

                if (connected.value) {
                    return;
                }

                connected.value = true;

                if (!appMounted) {
                    // 이미 마운트된 앱이 있으면 먼저 언마운트
                    // HMR(Hot Module Replacement)로 인해 main.ts가 재로드될 때 새로운 앱 인스턴스가 생성되면서 기존에 마운트된 앱 위에 다시 마운트하려고 시도하는 문제를 방지
                    // 개발 환경에서 빌링 페이지 갔다가 브라우저 뒤로가기 했을 때 에러 발생 문제 해결
                    const existingApp =
                        document.querySelector("#app")?.__vue_app__;
                    if (existingApp) {
                        existingApp.unmount();
                    }

                    app.use(router);
                    app.mount("#app");
                    appMounted = true;
                }

                // AOS 초기화
                app.mixin({
                    mounted() {
                        AOS.init({
                            offset: 30,
                            duration: 500,
                            easing: "ease-in-out",
                            once: true,
                        }); // AOS 초기화
                    },
                });
            },
        },
    },
    ETC_CONFIG,
);

skapi.version();

async function getEndpoints() {
    let endpoints = await Promise.all([
        skapi.admin_endpoint,
        skapi.record_endpoint,
    ]);
    let admin_private_endpoint = endpoints[0].admin_private; // https://.../
    let admin_public_endpoint = endpoints[0].admin_public; // https://.../
    let record_private_endpoint = endpoints[1].record_private; // https://.../

    let ep = {
        admin_private_endpoint,
        admin_public_endpoint,
        record_private_endpoint,
    };
    return ep;
}

export const ENDPOINTS = getEndpoints();

const urlParams = new URLSearchParams(window.location.search);

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID; // Replace this with your actual client ID

export const openIdLoginError = ref<string | null>(null);

(async () => {
    if (urlParams.get("code")) {
        // When the webpage is loaded, check if it's redirected from the Google login page.
        fullScreenLoader();

        const REDIRECT_URL = window.location.href.split("?")[0]; // Current URL to redirect back from Google login page
        const code = urlParams.get("code");

        function removeLoader() {
            removeFullScreenLoader();

            // remove get parameters from URL
            const newUrl = window.location.pathname;

            // Use router to replace URL without reloading
            router.replace(newUrl);
        }

        // Safely retrieve access token using clientSecretRequest
        const csrData = skapi.clientSecretRequest({
            clientSecretName: "ggl",
            url: "https://oauth2.googleapis.com/token",
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            data: {
                code: code,
                client_id: GOOGLE_CLIENT_ID,
                client_secret: "$CLIENT_SECRET",
                redirect_uri: REDIRECT_URL,
                grant_type: "authorization_code",
            },
        });

        let data = await csrData.catch((err) => {
            if (!(err instanceof SkapiError)) {
                removeLoader();
                throw err;
            }

            // INVALID_REQUEST는 clientSecretRequest 에러
            if (err?.code === "INVALID_REQUEST") {
                openIdLoginError.value =
                    err.message || "Invalid request for Google login.";
                removeLoader();
                return;
            }

            // openIdLogin에서 throw된 에러면 이미 메시지 설정됨, 글로벌 핸들러로 전파
            // clientSecretRequest의 예상치 못한 에러는 메시지 설정 후 전파
            if (!openIdLoginError.value) {
                openIdLoginError.value =
                    "Google login failed. Please try again.";
                removeLoader();
            }

            throw err;
        });

        // data.then(async (data: any) => {
        // await router.isReady();

        if (data.error) {
            if (process.env.NODE_ENV === "development") {
                console.error(data);
            }

            // Google OAuth error 처리
            let errorData = data.error;
            if (typeof errorData === "string") {
                try {
                    errorData = JSON.parse(errorData);
                } catch (e) {
                    errorData = { error: data.error };
                }
            }

            switch (errorData.error) {
                case "invalid_grant":
                    openIdLoginError.value =
                        "Google login session expired. Please try again.";
                    break;
                case "access_denied":
                    openIdLoginError.value = "Google login was denied.";
                    break;
                default:
                    openIdLoginError.value =
                        "Google login failed. Please try again.";
            }

            removeLoader();
            return;
        }

        openIdLoginError.value = null;

        // Use openIdLogin to log in
        try {
            await skapi.openIdLogin({
                id: "by_skapi",
                token: data.access_token,
            });
            // 로더를 제거하지 않고 페이지 이동 (새 페이지 로드까지 로딩 유지)
            window.location.href = "/my-services";
        } catch (err: any) {
            if (!(err instanceof SkapiError)) throw err;

            if (err.code === "ACCOUNT_EXISTS") {
                let merge = confirm(
                    "An account with this Google account already exists.\nWould you like to merge accounts?\nOnce merged, you cannot login from the previous method.",
                );
                if (merge) {
                    await skapi.openIdLogin({
                        id: "by_skapi",
                        token: data.access_token,
                        merge: ["name"],
                    });
                }
                // 병합 여부와 관계없이 로더를 제거하지 않고 페이지 이동
                window.location.href = "/my-services";
                return;
            }

            if (
                err.message === "Account needs to be confirmed." ||
                err.message === "The account needs to be confirmed."
            ) {
                openIdLoginError.value =
                    "Your account needs to be confirmed. Please check your email for the confirmation link.";
                removeLoader();
                return;
            }

            // 처리되지 않은 openIdLogin 에러
            openIdLoginError.value = "Login failed. Please try again.";
            removeLoader();

            throw err;
        }

        // }).catch((err) => {

        // // 에러 구분: clientSecretRequest vs openIdLogin
        // if (process.env.NODE_ENV === "development") {
        //     console.error("Google login error:", err);
        // }

        // // INVALID_REQUEST는 clientSecretRequest 에러
        // if (err?.code === "INVALID_REQUEST") {
        //     openIdLoginError.value =
        //         err.message || "Invalid request for Google login.";
        //     removeLoader();
        //     return;
        // }

        // // openIdLogin에서 throw된 에러면 이미 메시지 설정됨, 글로벌 핸들러로 전파
        // // clientSecretRequest의 예상치 못한 에러는 메시지 설정 후 전파
        // if (!openIdLoginError.value) {
        //     openIdLoginError.value = "Google login failed. Please try again.";
        //     removeLoader();
        // }

        // throw err;

        // });
    }
})();

// Source map 캐시
const sourceMapCache = new Map<string, SourceMapConsumer>();

// Source map을 로드하고 캐싱하는 함수
async function loadSourceMap(url: string): Promise<SourceMapConsumer | null> {
    if (sourceMapCache.has(url)) {
        return sourceMapCache.get(url)!;
    }

    try {
        const mapUrl = url + ".map";
        const response = await fetch(mapUrl);
        if (!response.ok) return null;

        const mapData = await response.json();
        const consumer = await new SourceMapConsumer(mapData);
        sourceMapCache.set(url, consumer);
        return consumer;
    } catch (err) {
        console.error("Failed to load source map:", err);
        return null;
    }
}

// Stack trace를 원본 소스 위치로 매핑하는 함수
async function mapStackTrace(stack: string): Promise<string> {
    if (!stack) return stack;

    const lines = stack.split("\n");
    const mappedLines: string[] = [];

    for (const line of lines) {
        // URL과 위치 정보 추출 (예: https://www.skapi.com/assets/index-Dz6Am52W.js:50:31248)
        const match = line.match(/(https?:\/\/[^\s]+\.js):(\d+):(\d+)/);

        if (!match) {
            mappedLines.push(line);
            continue;
        }

        const [fullMatch, url, lineStr, columnStr] = match;
        const lineNum = parseInt(lineStr, 10);
        const columnNum = parseInt(columnStr, 10);

        try {
            const consumer = await loadSourceMap(url);
            if (consumer) {
                const original = consumer.originalPositionFor({
                    line: lineNum,
                    column: columnNum,
                });

                if (original.source && original.line !== null) {
                    // 원본 소스 경로에서 불필요한 부분 제거
                    const cleanSource = original.source
                        .replace(/^(webpack:\/\/\/|\.\/|\.\.\/)*/, "")
                        .replace(/^src\//, "");

                    const mappedLocation = `${cleanSource}:${original.line}:${original.column || 0}`;
                    const newLine = line.replace(fullMatch, mappedLocation);
                    mappedLines.push(newLine);
                    continue;
                }
            }
        } catch (err) {
            // 매핑 실패 시 원본 라인 유지
        }

        mappedLines.push(line);
    }

    return mappedLines.join("\n");
}

let makeErrorInfo = async (errorValue: unknown) => {
    let errorCode: string | undefined;
    let errorMessage: string;
    let errorStack: string;
    let additionalInfo: Record<string, unknown> = {};

    function safeStringify(obj: unknown, indent = 0): string {
        const cache = new Set();
        return JSON.stringify(
            obj,
            (key, value) => {
                if (typeof value === "object" && value !== null) {
                    if (cache.has(value)) return "[Circular]";
                    cache.add(value);
                }
                return value;
            },
            indent,
        );
    }

    // Stack trace 유효성 검증 함수
    function isValidStack(stack: string): boolean {
        if (!stack || stack.length < 10) return false;
        // 의미있는 stack trace는 파일경로, 줄번호, 또는 함수명을 포함
        // 단순히 "@" 같은 기호만 있는 경우는 무효
        const hasUsefulInfo =
            stack.includes("http") ||
            stack.includes(".js") ||
            stack.includes(".ts") ||
            stack.includes("line") ||
            stack.includes(":") ||
            /\w{3,}/.test(stack); // 3글자 이상의 단어가 있는지
        return hasUsefulInfo;
    }

    if (errorValue instanceof Error) {
        errorCode = (errorValue as any).code;
        errorMessage = errorValue.message;
        const rawStack = errorValue.stack || "";

        // Stack trace 매핑
        const mappedStack = await mapStackTrace(rawStack);

        errorStack = isValidStack(rawStack)
            ? mappedStack
            : `Invalid stack trace (minified code). Error: ${errorValue.name}: ${errorValue.message}`;

        if (
            errorMessage.includes("is not iterable") ||
            errorMessage.includes("not a function")
        ) {
            additionalInfo.errorType = "iteration_error";
            additionalInfo.errorName = errorValue.name;
            additionalInfo.errorProps = Object.getOwnPropertyNames(
                errorValue,
            ).reduce(
                (acc, key) => {
                    acc[key] = (errorValue as any)[key];
                    return acc;
                },
                {} as Record<string, unknown>,
            );
        }
    } else if (typeof errorValue === "string") {
        errorMessage = errorValue;
        errorStack = "No stack trace";
    } else if (errorValue && typeof errorValue === "object") {
        if (errorValue instanceof Event) {
            return null; // 무시
        }

        const errObj = errorValue as Record<string, unknown>;
        errorCode = typeof errObj.code === "string" ? errObj.code : undefined;

        // Error 객체의 실제 정보를 추출
        if (typeof errObj.message === "string" && errObj.message) {
            errorMessage = errObj.message;
        } else {
            // message가 없으면 객체의 모든 속성을 추출
            const stringified = safeStringify(errObj);
            errorMessage =
                stringified === "{}" ? "Unknown error object" : stringified;
        }

        const rawStack = typeof errObj.stack === "string" ? errObj.stack : "";

        // Stack trace 매핑
        const mappedStack = rawStack ? await mapStackTrace(rawStack) : "";

        errorStack = isValidStack(rawStack)
            ? mappedStack
            : rawStack
              ? `Invalid stack trace (minified code). Original: ${rawStack}`
              : "No stack trace";
    } else {
        errorMessage = String(errorValue);
        errorStack = "No stack trace";
    }

    const sendInfo = {
        error: {
            code: errorCode,
            message: errorMessage,
        },
        stack: errorStack,
        url: window.location.href,
        urlParams: Object.fromEntries(
            new URLSearchParams(window.location.search),
        ),
        userAgent: navigator.userAgent,
        timestamp: Date.now(),
        ...(Object.keys(additionalInfo).length > 0 && { additionalInfo }),
    };

    const senderInfo = {
        name: user?.name || "Guest",
        email: user?.email || "front@broadwayinc.com",
    };

    return { senderInfo, sendInfo };
};

// 에러 전달 순서: Vue errorHandler -> window unhandledrejection -> window error
// 에러 핸들러 (동기)
// Vue 컴포넌트 내부에서 발생한 에러만 처리 -> 항상 우리 코드임
app.config.errorHandler = (err, vm, info) => {
    throw err;
};

// 에러 핸들러 (비동기)
window.addEventListener("unhandledrejection", (event) => {
    const { reason } = event;

    // 브라우저 자체에서 발생하는 외부 에러 필터링
    function isOurCodeInStack(stack) {
        if (!stack) return false;
        return stack.includes(window.location.hostname);
    }

    if (reason instanceof Error && isOurCodeInStack(reason.stack)) {
        // 우리 코드에서 발생했지만 보고할 필요 없는 에러 필터링
        const errorMsg = reason.message || "";

        // 네트워크 관련 에러 (우리 버그가 아님)
        if (
            errorMsg.includes("Failed to fetch") ||
            errorMsg.includes("NetworkError") ||
            errorMsg.includes("Network request failed") ||
            errorMsg.includes("Load failed")
        )
            return;

        // Chrome 확장 프로그램 (이미 isOurCodeInStack에서 걸러지지만 방어적 체크)
        if (
            errorMsg.includes("chrome-extension://") ||
            reason.stack?.includes("chrome-extension://")
        )
            return;

        throw event.reason;
    }
});

window.addEventListener("error", async (event) => {
    if (event.error instanceof SkapiError) {
        alert(event.error.message);
        return;
    }

    // 브라우저 자체에서 발생하는 외부 에러 필터링
    const isOurCode = (filename) => {
        if (!filename) return false;
        const origin = window.location.origin;
        return filename.startsWith(origin) && !filename.includes(".map");
    };

    if (event.error && isOurCode(event.filename)) {
        const result = await makeErrorInfo(event.error);
        if (!result) return;
        const { senderInfo, sendInfo } = result;

        if (import.meta.env.VITE_MODE === "development") {
            // localhost나 로컬 네트워크 주소에서는 에러 메일 안 보냄
            const hostname = window.location.hostname;
            if (
                hostname === "localhost" ||
                hostname.startsWith("127.") ||
                hostname.startsWith("192.168.") ||
                hostname.startsWith("172.") ||
                hostname.startsWith("10.")
            ) {
                console.log("[Global Error] Sender Info:", senderInfo);
                console.log("[Global Error] Info to Send:", sendInfo);
                return;
            }
            // skapi.app 등 실제 도메인에서는 계속 진행 (에러 메일 전송)
        }

        // else {
        // 일반적인 네트워크/브라우저 에러 필터링
        const errorMsg = sendInfo.error.message || "";

        // 개발환경 관련 에러
        if (
            sendInfo.stack.includes(
                "TypeError: Cannot read properties of null (reading 'nextSibling')s",
            ) ||
            errorMsg.includes(
                "Cannot read properties of null (reading 'nextSibling')",
            )
        )
            return;

        // 네트워크 관련 에러
        if (
            errorMsg.includes("Failed to fetch") ||
            errorMsg.toLowerCase().includes("network") ||
            errorMsg.includes("Load failed")
        )
            return;

        // Chrome 확장 프로그램 관련
        if (
            errorMsg.includes("chrome-extension://") ||
            sendInfo.stack.includes("chrome-extension://")
        )
            return;

        // sendInquiry 자체 에러 방지 (무한 루프 방지)
        if (
            sendInfo.stack.includes("sendInquiry()") ||
            errorMsg.includes("sendInquiry")
        )
            return;

        // 에러 인쿼리 전송
        skapi
            .sendInquiry({
                name: senderInfo.name,
                email: senderInfo.email,
                subject: "Frontend Global Error Report",
                message: JSON.stringify(sendInfo, null, 2),
            })
            .catch((err) => {
                // 3초 있다가 다시 시도
                // setTimeout(()=>{
                //     skapi.sendInquiry({
                //         name: senderInfo.name,
                //         email: senderInfo.email,
                //         subject: "Frontend Global Error Report - Retry",
                //         message: JSON.stringify(sendInfo, null, 2),
                //     });
                // },3000);
            });
        //}
    }
});

// gtag 클릭 이벤트 추적
trackAutoBind(() => user?.user_id || "guest");
